import { create } from 'zustand'
import type { AgentProgressEvent, AgentCompleteEvent, SearchActivityEvent, Question, Run } from '../types'

// localStorage key for persisting active run
const ACTIVE_RUN_KEY = 'cenva_active_run'

interface ActiveRunStorage {
  runId: number
  questionId: number
  questionTitle: string
  paperTrade: boolean
  startedAt: string
}

interface RunnerState {
  isOpen: boolean
  isMinimized: boolean
  isRunning: boolean
  runId: number | null
  questionId: number | null
  questionTitle: string
  paperTrade: boolean
  stages: AgentStage[]
  searchActivity: SearchActivity[]
  result: RunResult | null
  error: string | null
  startedAt: string | null
}

interface AgentStage {
  name: string
  status: 'pending' | 'running' | 'complete' | 'error'
  progress: number
  // Enhanced fields from agent completion
  prob?: number
  confidence?: number
  direction?: 'bullish' | 'bearish' | 'neutral'
  keyFindings?: string[]
  evidenceCount?: number
  currentSearch?: string
}

interface SearchActivity {
  agent: string
  query: string
  status: 'started' | 'complete'
  resultCount?: number
  highlight?: string
  timestamp: number
}

interface TradeResult {
  executed: boolean
  order_id?: number
  side?: string
  qty?: number
  price?: number
  usd_notional?: number
  traded_question?: string
  reason?: string
}

interface RunResult {
  run_id: number
  final_prob: number
  confidence: number
  decision: string
  trade?: TradeResult | null
}

// Quick filter preset type
type QuickFilter = 'all' | 'high_edge' | 'closing_soon' | 'high_liquidity' | 'never_predicted'

interface AppState {
  // Question filters
  filters: {
    category: string | null
    status: string | null
    hasRun: boolean | null
    hasPosition: boolean | null
    search: string
    sort: string
    quickFilter: QuickFilter
  }
  setFilters: (filters: Partial<AppState['filters']>) => void

  // Runner panel (minimizable, persists across navigation)
  runner: RunnerState
  openRunner: (questionId: number, title: string, paperTrade?: boolean) => void
  closeRunner: () => void
  minimizeRunner: () => void
  maximizeRunner: () => void
  setRunnerRunId: (runId: number, startedAt: string) => void
  updateRunnerStage: (event: AgentProgressEvent) => void
  completeRunnerAgent: (event: AgentCompleteEvent) => void
  addSearchActivity: (event: SearchActivityEvent) => void
  setRunnerResult: (result: RunResult) => void
  setRunnerError: (error: string) => void
  resetRunner: () => void
  checkForActiveRun: () => ActiveRunStorage | null
  clearActiveRun: () => void
  restoreRunner: (data: ActiveRunStorage, result?: RunResult) => void

  // Selected question for detail view
  selectedQuestion: Question | null
  setSelectedQuestion: (q: Question | null) => void

  // Active run being viewed
  selectedRun: Run | null
  setSelectedRun: (r: Run | null) => void

  // Theme (for future use)
  theme: 'dark'
}

const defaultStages: AgentStage[] = [
  { name: 'planner', status: 'pending', progress: 0 },
  { name: 'news', status: 'pending', progress: 0 },
  { name: 'quant', status: 'pending', progress: 0 },
  { name: 'skeptic', status: 'pending', progress: 0 },
  { name: 'synthesis', status: 'pending', progress: 0 },
]

// Helper to persist active run to localStorage
function persistActiveRun(data: ActiveRunStorage | null) {
  if (data) {
    localStorage.setItem(ACTIVE_RUN_KEY, JSON.stringify(data))
  } else {
    localStorage.removeItem(ACTIVE_RUN_KEY)
  }
}

// Helper to read active run from localStorage
function readActiveRun(): ActiveRunStorage | null {
  try {
    const stored = localStorage.getItem(ACTIVE_RUN_KEY)
    if (stored) {
      return JSON.parse(stored) as ActiveRunStorage
    }
  } catch {
    // Invalid JSON, clear it
    localStorage.removeItem(ACTIVE_RUN_KEY)
  }
  return null
}

export const useAppStore = create<AppState>((set, get) => ({
  // Question filters
  filters: {
    category: null,
    status: null,
    hasRun: null,
    hasPosition: null,
    search: '',
    sort: 'recent',
    quickFilter: 'all',
  },
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),

  // Runner panel
  runner: {
    isOpen: false,
    isMinimized: false,
    isRunning: false,
    runId: null,
    questionId: null,
    questionTitle: '',
    paperTrade: false,
    stages: [...defaultStages],
    searchActivity: [],
    result: null,
    error: null,
    startedAt: null,
  },

  openRunner: (questionId, title, paperTrade = false) =>
    set({
      runner: {
        isOpen: true,
        isMinimized: false,
        isRunning: true,
        runId: null, // Will be set when SSE sends start event
        questionId,
        questionTitle: title,
        paperTrade,
        stages: defaultStages.map((s) => ({ ...s, status: 'pending', progress: 0 })),
        searchActivity: [],
        result: null,
        error: null,
        startedAt: null,
      },
    }),

  closeRunner: () => {
    // Clear localStorage when explicitly closing
    persistActiveRun(null)
    set((state) => ({
      runner: { ...state.runner, isOpen: false, isMinimized: false },
    }))
  },

  minimizeRunner: () =>
    set((state) => ({
      runner: { ...state.runner, isMinimized: true },
    })),

  maximizeRunner: () =>
    set((state) => ({
      runner: { ...state.runner, isMinimized: false },
    })),

  // Called when SSE sends start event with run_id
  setRunnerRunId: (runId, startedAt) => {
    const state = get()
    // Persist to localStorage for recovery
    if (state.runner.questionId) {
      persistActiveRun({
        runId,
        questionId: state.runner.questionId,
        questionTitle: state.runner.questionTitle,
        paperTrade: state.runner.paperTrade,
        startedAt,
      })
    }
    set((state) => ({
      runner: { ...state.runner, runId, startedAt },
    }))
  },

  updateRunnerStage: (event) =>
    set((state) => ({
      runner: {
        ...state.runner,
        stages: state.runner.stages.map((stage) =>
          stage.name === event.agent
            ? {
                ...stage,
                status: event.status as AgentStage['status'],
                progress: event.progress,
                currentSearch: event.current_search,
              }
            : stage
        ),
      },
    })),

  completeRunnerAgent: (event) =>
    set((state) => ({
      runner: {
        ...state.runner,
        stages: state.runner.stages.map((stage) =>
          stage.name === event.agent
            ? {
                ...stage,
                status: 'complete' as const,
                progress: event.progress,
                prob: event.prob ?? undefined,
                confidence: event.confidence ?? undefined,
                direction: event.direction,
                keyFindings: event.key_findings,
                evidenceCount: event.evidence_count,
                currentSearch: undefined,
              }
            : stage
        ),
      },
    })),

  addSearchActivity: (event) =>
    set((state) => ({
      runner: {
        ...state.runner,
        searchActivity: [
          { ...event, timestamp: Date.now() },
          ...state.runner.searchActivity.slice(0, 9), // Keep last 10
        ],
      },
    })),

  setRunnerResult: (result) => {
    // Clear localStorage when run completes
    persistActiveRun(null)
    set((state) => ({
      runner: {
        ...state.runner,
        isRunning: false,
        result,
        stages: state.runner.stages.map((s) => ({ ...s, status: 'complete', progress: 100 })),
      },
    }))
  },

  setRunnerError: (error) => {
    // Clear localStorage on error
    persistActiveRun(null)
    set((state) => ({
      runner: {
        ...state.runner,
        isRunning: false,
        error,
      },
    }))
  },

  resetRunner: () => {
    persistActiveRun(null)
    set({
      runner: {
        isOpen: false,
        isMinimized: false,
        isRunning: false,
        runId: null,
        questionId: null,
        questionTitle: '',
        paperTrade: false,
        stages: [...defaultStages],
        searchActivity: [],
        result: null,
        error: null,
        startedAt: null,
      },
    })
  },

  // Check localStorage for an active run (called on app load)
  checkForActiveRun: () => {
    return readActiveRun()
  },

  // Clear active run from storage
  clearActiveRun: () => {
    persistActiveRun(null)
  },

  // Restore runner state from storage (used after polling run status)
  restoreRunner: (data, result) => {
    if (result) {
      // Run completed - show result
      persistActiveRun(null)
      set({
        runner: {
          isOpen: true,
          isMinimized: false,
          isRunning: false,
          runId: data.runId,
          questionId: data.questionId,
          questionTitle: data.questionTitle,
          paperTrade: data.paperTrade,
          stages: defaultStages.map((s) => ({ ...s, status: 'complete', progress: 100 })),
          searchActivity: [],
          result,
          error: null,
          startedAt: data.startedAt,
        },
      })
    } else {
      // Run still in progress - show minimized indicator
      set({
        runner: {
          isOpen: true,
          isMinimized: true,
          isRunning: true,
          runId: data.runId,
          questionId: data.questionId,
          questionTitle: data.questionTitle,
          paperTrade: data.paperTrade,
          stages: defaultStages.map((s) => ({ ...s, status: 'running', progress: 50 })),
          searchActivity: [],
          result: null,
          error: null,
          startedAt: data.startedAt,
        },
      })
    }
  },

  // Selected items
  selectedQuestion: null,
  setSelectedQuestion: (q) => set({ selectedQuestion: q }),

  selectedRun: null,
  setSelectedRun: (r) => set({ selectedRun: r }),

  theme: 'dark',
}))
